<script setup lang="ts">
import { transformPrice } from '@/helpers/priceHelper'
import type { Locale, SearchHouse } from 'lc-services/types'

const { house } = defineProps<{
  house: SearchHouse
}>()
defineEmits<{ close: []; 'track-house': [houseId: number] }>()

const { isMobile } = useBreakpoint()
const localePath = useLocalePath()
const { t, locale } = useI18n<unknown, Locale>()
const { userIsAdmin, userIsPartner } = useAuth()
const { currency } = useUser()

const { setCurrentHouseFavorite } = useSearchWishlist()
const { hasDates } = useSearchDateFilters()

const details = ['capacity', 'bedrooms', 'bathrooms'] as const

const currentPrice = computed(() => {
  const isValidPeriod =
    (house?.fullyCovered && house?.allMinimumDurationValid) || false

  return transformPrice({
    currency: currency.value,
    hasDates: hasDates.value,
    hit: house,
    i18n: {
      locale,
      t,
    },
    isValidPeriod,
    userIsAdmin: userIsAdmin.value,
    userIsPartner: userIsPartner.value,
  })
})

const handleClick = () => {
  setCurrentHouseFavorite(
    {
      id: house.id,
      name: house.name,
      iconicCollection: true,
      publicPrice:
        currentPrice.value.isValid && currentPrice.value.value
          ? currentPrice.value.value
          : undefined,
    },
    'map',
  )
}
</script>

<template>
  <div>
    <NuxtLink
      :href="
        localePath({
          name: 'house-slug',
          params: { slug: house.slug[locale] },
          query: { 'private-token': house.private_token },
        })
      "
      class="hover:no-underline"
      :target="isMobile ? '_self' : '_blank'"
      @click="$emit('track-house', house.id)"
    >
      <div class="flex flex-row items-center">
        <div class="relative w-[130px] flex-none">
          <BaseNuxtImg
            :alt="house.name"
            :src="house.photos[0]"
            :height="140"
            :width="130"
          />
          <div
            class="absolute left-2 top-2 rounded-full bg-gray-700/50 p-1 text-white"
            @click.prevent.stop="$emit('close')"
          >
            <BaseIcon name="close" :size="1" />
          </div>
        </div>
        <div class="flex h-full flex-col justify-center p-3 text-left">
          <div class="absolute right-1 top-1 z-50">
            <WishlistToggle
              v-slot="slotProps"
              :house-id="house.id"
              :house-name="house.name"
              @callback-click="handleClick"
            >
              <HouseWishlistButton
                class="rounded-full bg-white p-2 text-gray-700"
                :is-active="slotProps.active"
              />
            </WishlistToggle>
          </div>
          <h6 class="m-0 text-base leading-6 text-gray-700">
            {{ house.name }}
          </h6>
          <div class="text-xs text-gray-500">
            <span
              v-for="(detail, i) in details"
              :key="i"
              :class="`
                after:content-[''] after:w-0.5 after:h-0.5 after:mx-1
                after:inline-block after:align-middle
                after:bg-gray-600 after:rounded-full
                last:after:hidden
              `"
            >
              {{ house[detail] }}
              {{ $t(`house.${detail}Key`, house[detail]) }}
            </span>
          </div>
          <div class="mt-2 text-xs font-bold text-gray-700">
            {{ currentPrice.text }}
          </div>
        </div>
      </div>
    </NuxtLink>
  </div>
</template>
